import { Component, inject, Input } from '@angular/core';
import { KPI } from '../../../@core/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { I18nService } from '../../../@core/utils';

export interface AssetItem extends KPI.Asset {
  id: number;
  perimeter: KPI.Perimeter;
  quantity: number;
  quantity_unit: KPI.QuantityUnit;
  label: string;
  investments: KPI.Investment[];
}

@Component({
  selector: 'app-asset-item',
  standalone: true,
  template: `
    <div class="row g-0">
      <div class="col-2 p-2">
        <img [src]="imgUrl()" alt="{{asset.label}}" class="img-thumbnail p-0"
             style="width: 100px; height: 100px; object-fit: cover" />
      </div>
      <div class="col-9 ms-1 pt-2">
        <div class="fs-6">{{ asset.label }}</div>
        <div></div>
        <div>{{ "Qty" | translate }}:  {{ assetQuantity() }} - <span style="color: #00729a">{{ assetPerimeterText() }}</span>
        </div>
        <div>{{ assetInvestmentText() }}</div>
      </div>
    </div>
  `,
  imports: [
    TranslateModule,
  ],
  styles: `
    :host {
      width: 100%;
      display: block
    }
  `,
})
export class AssetItemComponent {
  @Input() asset: AssetItem;

  private translate = inject(TranslateService);
  private i18n = inject(I18nService);

  imgUrl(): string {
    const images = this.asset.pictures;
    if (!images || images.length === 0) {
      return 'assets/images/placeholder.png';
    }
    const image = images.find(i => i.thumbnail) || images[0];
    if (image && image.thumbnail) {
      return image.thumbnail;
    }
  }

  assetQuantity(): string {
    const quantity = this.i18n.formatNumber(this.asset.quantity);
    const unit = this.asset.quantity_unit;
    return unit ? `${quantity} ${unit.name}` : `${quantity} u`;
  }

  assetPerimeterText(): string {
    const perimeter = this.asset.perimeter;
    return (<KPI.Perimeter>perimeter)?.name || '-';
  }

  assetInvestmentText(): string {
    const investments = this.asset.investments;
    if (!investments || investments.length === 0) return this.translate.instant('No investment plan available');
    if (investments.length === 1) {
      const inv = investments[0],
        amount = this.i18n.formatPrice(inv.current_price).join(' ');
      return this.translate.instant('1 investment in {year}: {amount}', {year: inv.final_schedule_to, amount });
    } else {
      const total = investments.reduce((sum, inv) => sum + Number.parseInt(<string><unknown>inv.current_price), 0),
        amount = this.i18n.formatPrice(total).join(' ');
      return this.translate.instant('{count} investments, total: {amount}', {count: investments.length, amount });
    }
  }

}
